/**
 * FileName : function.ts
 * ProjectName : admin
 * Author : terrorblade
 * Created Date: 2021-09-28 18:09:46
 * Description :
 * -----
 * Last Modified:
 * Modified By :
 * -----
 * Copyright (c) 2021 Magina Corporation. All rights reserved.
 */
import { isPlainObject } from 'lodash';

/**
 * 添加根节点数转换函数
 */
import { UserNode } from 'src/api/user/type';

export type CascaderData = {
  value: string;
  label: string;
  children?: CascaderData[];
}

/**
 *
 * @param nodeList UserNode
 * @param rootNode CascaderData
 * @param parentId parentId父级Id
 * @param flag 如果一级节点有父级Id，但是没返回情况
 */

export const nodeDataFormatCascader = (nodeList: UserNode[], rootNode: CascaderData[], parentId: string | null = null, flag: boolean = false) => {
    let node:UserNode[] = nodeList.filter((v: UserNode) => v.PARENTID === parentId);

    if (!flag && !node.length) {
        node = nodeList.filter((v: UserNode) => v.PARENTID && nodeList.every((o) => v.PARENTID !== o.ID));
    }

    node.forEach((item:UserNode) => {
        const findNode = nodeList.find((v:UserNode) => v.PARENTID === item.ID);
        const obj: CascaderData = {
            label: item.NAME,
            value: item.ID,
        };

        if (findNode) {
            obj.children = [];
            rootNode.push(obj);
            nodeDataFormatCascader(nodeList, obj.children, item.ID);
        } else {
            rootNode.push(obj);
        }
    });
};

type NodeListData = {
    nodeList: UserNode[];
    rootNode: CascaderData[];
    parentId?: string|null;
    id?: string;
}

export const nodeTreeFormatCascader = ({nodeList, rootNode, parentId, id}:NodeListData) => {
    if (!parentId && !id) {return;}
    nodeList.forEach((v: UserNode) => {
        let item: UserNode|null = null;

        if (typeof id !== 'undefined') {
            if (v.ID === id) {
                item = v;
            }
        }
        if (typeof parentId !== 'undefined') {
            if (v.PARENTID === parentId) {
                item = v;
            }
        }
        if (item) {
            const findNode = nodeList.find((o:UserNode) => o.PARENTID === item?.ID);
            const obj: CascaderData = {
                label: item.NAME,
                value: item.ID,
            };

            if (findNode) {
                obj.children = [];
                rootNode.push(obj);
                nodeTreeFormatCascader({
                    nodeList,
                    rootNode: obj.children,
                    parentId: item.ID,
                });
            } else {
                rootNode.push(obj);
            }
        }
    });
};

import {NodeByLoginUserData} from 'src/api/common/type';
export const findCascaderInitData = (data:NodeByLoginUserData[], id:string, initData: string[]):void => {
    const item = data.find((v) => v.ID === id);

    if (item) {
        initData.unshift(id);
        findCascaderInitData(data, item.PARENTID, initData);
    }
};

export const findCascaderItem = (data: CascaderData[], keys: string[]):CascaderData|undefined => {
    const item = data.find((v) => v.value === keys[0]);

    if (item && keys.length === 1) {
        return item;
    } else if (item && keys.length > 1) {
        return findCascaderItem(item.children || [], keys.slice(1));
    }
};

import { Key } from 'rc-tree/es/interface';
// 原始数据中获取选中数据
export const devicedIntersection = (data: any[], keyList: Key[], nodeList: any[], itKey: string = 'deviceGuid') => {
    data.forEach((v) => {
        const item = keyList.find((key) => v[itKey] === key);

        if (item) {
            nodeList.push(v);
        }
        if (v.children && v.children.length) {
            devicedIntersection(v.children, keyList, nodeList, itKey);
        }
    });
};
// 将设备树数据不同类型的树设置为禁选
export const forbiddenNodeCheck = (treeData: any[], deviceModel: string, type: boolean, typeKey:string, isChild?:boolean) => treeData.map((v) => {
    if (!type) {
        if (v[typeKey] !== deviceModel && deviceModel) {
            v.disabled = true;
        } else {
            v.disabled = false;
        }
    } else {
        if ((v.nodeType === '1' || v.nodeType === '4' || v.nodeType === '5') && v.children && v.children.length) {
            v.children = forbiddenNodeCheck(v.children, deviceModel, type, typeKey, true);
        }
        if (!parseInt(v.nodeType, 10) || (parseInt(v.nodeType, 10) === 1 && isChild)) {
            if (v[typeKey] !== deviceModel && deviceModel) {
                v.disabled = true;
            } else {
                v.disabled = false;
            }
        }

    }
    return v;
});

import {EquipTreeNode} from 'src/components/business/deviceTree/index';
export const forbiddenGroupNodeCheck = (treeData: EquipTreeNode[], key: string, flag:boolean = true):EquipTreeNode[] => treeData.map((v) => {
    if (v.deviceGuid !== key) {
        v.disabled = flag;
        v.children?.forEach((child) => {
            child.disabled = flag;
        });
    }
    return v;
});
import { EquipTreeNode as EquipGisTreeNode } from 'src/components/business/deviceTree/mapTree';

export const forbiddenGisGroupNodeCheck = (treeData: EquipGisTreeNode[], key: string, flag:boolean = true):EquipGisTreeNode[] => treeData.map((v) => {
    if (v.deviceGuid !== key) {
        v.disabled = flag;
        v.children?.forEach((child) => {
            child.disabled = flag;
        });
    }
    return v;
});


// 随抄内容数据转换
import { ProtocolDataList } from 'src/api/AmiFunction/onDemandReading/type';
export type NodeChildProperty = {
    key: string;
    no: string;
    id: string;
    text: string;
    title: string;
    tooltip: string; // 气泡提示
    dataType: string|null; // 下发参数类型
    resultDataType: string;
    returnType?:string; // 返回值展示类型  0:输出参数    1：输入参数
    protocalCommand: string;
    paramSn: string|null; // PROTOCAL_BYTE_DESE_SN 来自表 CFG_PROTOCAL_STATUS_WORD 的 COMMAND_DEFINE_SN  说明参数每个字节的含义
    defaultValue: string|null;
    maxValue: string|null;
    minValue: string|null;
    dataFormat: string | null;
    parentId: string;
    bitSQ?:string;
    inputParamCount: number;
    outputParamCount: number;
    PARAMETER_FLAG?:string;
    unit: string;
    nodeType:number;
    commandFN: string;
    priority: string;
}
export type NodeProperty = {
    title: String;
    key: string;
    propertyLen?: number;
    children: NodeChildProperty[];
}
export type TypeNodeAttr = {
    [key: string]: NodeProperty
}
export const constructCommandData = (list: ProtocolDataList[] = [], typeNodeMap: TypeNodeAttr = {}): NodeProperty[] => {
    if (!list.length) {
        return [];
    }
    for (const item of list) {
        const type = item.TYPE;
        const SCHEME_NAME = item.SCHEME_NAME;
        let parentNode:NodeProperty|null = null;
        let key = type;

        if (parseInt(type, 10) === 9) {
            if (!typeNodeMap.hasOwnProperty(`${type}${SCHEME_NAME}`)) {
                typeNodeMap[`${type}${SCHEME_NAME}`] = {title: SCHEME_NAME, key: `${type}${SCHEME_NAME}`, children: []};
            }
            parentNode = typeNodeMap[`${type}${SCHEME_NAME}`];
            key = `${type}${SCHEME_NAME}`;
        } else {
            parentNode = typeNodeMap[type];
        }

        if (parentNode && isPlainObject(parentNode) && typeof parentNode.children !== undefined) {
            const isCheckCommon = parentNode.children.find((v) => v.key === key + item['NO']);

            if (isCheckCommon) { // 如果存在该协议项，跳过
                continue;
            }
            parentNode.children.push({
                key: key + item['NO'],
                no: item['NO'],
                id: item['NO'],
                text: item.NAME,
                title: `${item['NAME']}(${item['AFN']}-${item['FN']})`,
                tooltip: item['NAME'] + '(' + item['AFN'] + ')', // 气泡提示(老系统用到)
                dataType: item['DATA_TYPE'], // 下发参数类型
                resultDataType: item['RESULT_DATA_TYPE'],
                // returnType:list[i]["PARAMETER_FLAG"],            //返回值展示类型  0:输出参数    1：输入参数
                protocalCommand: item['SN_PROTOCAL_COMMAND'],
                paramSn: item['PROTOCAL_BYTE_DESE_SN'], // PROTOCAL_BYTE_DESE_SN 来自表 CFG_PROTOCAL_STATUS_WORD 的 COMMAND_DEFINE_SN  说明参数每个字节的含义
                defaultValue: item['DEFAULT_VALUE'],
                maxValue: item['MAX_VALUE'],
                minValue: item['MIN_VALUE'],
                dataFormat: item['DATA_FORMAT'],
                // bitSQ:list[i]["BITSQ"],
                inputParamCount: item['INPUT_PARAM_COUNT'],
                outputParamCount: item['OUTPUT_PARAM_COUNT'],
                // PARAMETER_FLAG:list[i]["PARAMETER_FLAG"],
                unit: item['UNIT'],
                nodeType: 1,
                commandFN: item['FN'],
                priority: item['PRIORITY']?.toString(),
                parentId: parentNode.key,
            });
        }
    }
    return Object.keys(typeNodeMap).map((key: string) => {
        if (typeNodeMap[key].children.length) {
            if (!typeNodeMap[key].propertyLen) {
                typeNodeMap[key].propertyLen = typeNodeMap[key].children.length;
                typeNodeMap[key].title = `${typeNodeMap[key].title}(${typeNodeMap[key].children.length})`;
            }
            return typeNodeMap[key];
        }
    })
        .filter((v) => v) as NodeProperty [];
};

export const findProtoData = (data: NodeProperty[], keyList: Key[]): NodeChildProperty[] => {
    let arr: NodeChildProperty[][] = [];

    data.forEach((v) => {
        const list = v.children.filter((item) => keyList.some((key) => key.toString() === item.key));

        arr.push(list);
    });
    return arr.flat();
};

// 获取所有数组下面的children
export const arrFormatChild = (arr:any[]) => {
    const list = [];

    for (const v of arr) {
        for (const o of v.children) {
            list.push(o);
        }
    }
    return list;
};

// 将数据变成select的value,name形式
export type Options = {
    value: string;
    name: string;
}
export const formatArrOptions = <T>(arr: T[], key: string, name: string, fn?:(item:T)=>string) => arr.map((v) => {
    if (typeof v[key] !== undefined && typeof v[name] !== undefined && v[name]) {
        return {
            value: v[key],
            name: typeof fn === 'function' ? fn(v) : v[name],
        };
    }
}).filter((v) => v) as Options[];

// 将数据变成select的value,name形式
import { OptionCoreData } from 'rc-select/es/interface';

export const formatOptions = <T>(arr: T[], key: string, name: string, fn?:(item:T)=>string) => arr.map((v) => {
    if (typeof v[key] !== undefined && typeof v[name] !== undefined && v[name]) {
        return {
            value: v[key],
            label: typeof fn === 'function' ? fn(v) : v[name],
        };
    }
}).filter((v) => v) as OptionCoreData[];

// 升级模块key,text,value合并
export interface optText extends Options {
    key: string;
}
export const keyTextFormatVal = <T extends Object> (val: T, keyText: { [key: string]: string }):optText[] => Object.keys(val).map((key: string) => ({
    value: val[key],
    name: keyText[key],
    key,
}));
import { ReactNode } from 'react';
export const queryTaskFormatTreeData = <T>(data: T[], groupTaskFormatTitle:(val:any)=>string|ReactNode) => data.map((v) => ({
    key: v['GROUP_ID'],
    title: groupTaskFormatTitle(v),
    ...v,
}));

import { message } from 'antd';
export const abnormalFn = async (fn: any, val?:any) => {
    try {
        if (typeof fn === 'function') {
            await fn(val);
        }
    } catch (error) {
        console.error(error);
        message.error(typeof error === 'string' ? error : 'Program exception error');
    }
};

/**
 * 线路转换
 */

import { MeterGroupList } from 'src/api/customer&Device/meterMgnt/type';
import { GetDstListData } from 'src/api/basicData/transformMgt/type';
import { valueArr } from './initDynamicForm';
export type OptGroupList = valueArr;
export const resCastOption = (res:MeterGroupList[]|GetDstListData[]):valueArr[] => {
    const obj = {};
    let id = 'CLASSICAL_DETAIL_GUID';
    let name = 'CLASSICAL_DETAIL_NAME';

    if (!res.length) {return [];}
    if ('ID' in res[0]) {
        id = 'ID';
        name = 'NAME';
    }

    res.forEach((v) => {
        if (obj[v.GNAME]) {
            obj[v.GNAME].children.push({
                value: v[id],
                name: v[name],
            });
        } else {
            obj[v.GNAME] = {
                label: v.GNAME,
                value: v[id],
                children: [],
            };
            obj[v.GNAME].children.push({
                value: v[id],
                name: v[name],
            });
        }
    });
    return Object.keys(obj).map((v) => obj[v]);
};

export const formatCastOption = <T extends Object = any>(res: T[], idKey:string, nameKey:string, parentKey:string, parentNameKey:string): valueArr[] => {
    const obj = {};

    res.forEach((v) => {
        if (obj[v[parentKey]]) {
            obj[v[parentKey]].children.push({
                value: v[idKey],
                name: v[nameKey],
            });
        } else {
            obj[v[parentKey]] = {
                label: v[parentNameKey],
                value: v[parentKey],
                children: [],
            };
            obj[v[parentKey]].children.push({
                value: v[idKey],
                name: v[nameKey],
            });
        }
    });
    return Object.keys(obj).map((v) => obj[v]);
};

// 递归查找多叉树结构数据
/**
 *
 * @param treeData // 多叉树数据
 * @param key // 查找的属性名
 * @param value // 查找的值
 * @param child // 子集属性名
 */

export const findManyTreeRow = (treeData: any[], key: string, value: any, child: string = 'children', list: any[]) => {
    for (const item of treeData) {
        if (item[key] === value && !item[child]) {
            return list.push(item);
        } else if (item[child] && item[child].length) {
            findManyTreeRow(item[child], key, value, child, list);
        }
    }
};

// 递归查找链式key值

export const findParentKeyArr = (arr: any[], childKey:string, parentKey:string, value:any, list:any[]) => {
    for (const item of arr) {
        if (item[childKey] && item[childKey] === value && item[parentKey]) {
            list.unshift(item[parentKey]);
            findParentKeyArr(arr, childKey, parentKey, item[parentKey], list);
        }
    }
};

export const arrayToStringSplit = (arr: string[]) => {
    let str = '';

    if (arr) {
        arr.map((v, index) => {
            if (index < arr.length - 1) {
                str = str + '\'' + v + '\',';
            } else {
                str = str + '\'' + v + '\'';
            }
        });
    }
    return str;
};

// 打印数据格式转换

export const printFormat = (data: any, format: any) => {
    const obj: any = {};

    Object.keys(format).forEach((key: string) => {
        if (typeof data[key] !== 'undefined') {
            obj[format[key]] = data[key];
        } else {
            obj[format[key]] = '';
        }
    });
    return obj;
};

type OptionRows = {
    label: string;
    value: string;
}

export const transformOptionType = (data: OptionRows[]): Options[] => data.map((v) => ({
    name: v.label,
    value: v.value,
}));

import { MenuTree } from 'src/api/homePage/type';


import { RoleTreeRows } from 'src/utils/const';
export const findMenuRole = (data: MenuTree[], roleList: RoleTreeRows[]) => {
    data.forEach((item) => {
        const roleRow = roleList.find((v) => v.key === item.menuKey);

        if (roleRow) {
            roleRow.value = true;
        }
        if (item.children) {
            findMenuRole(item.children, roleList);
        }
    });
};

export type RoleObject = {
    [key in string]: boolean;
}

export const formatRoleList = (roleList: RoleTreeRows[]):RoleObject => {
    const obj: RoleObject = {};

    roleList.forEach((v) => {
        obj[v.key] = v.value;
    });
    return obj;
};
